import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections, initialValues } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createAssignedBPPlan,
  getBPLinesLov,
  getBPPlanListByAssignedLines,
  getBusinessPartnerListWithoutPagination,
} from './queries';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { isEmpty } from '@utils/validationUtils';
import { getError } from '@utils/graph-utils';
import { extractBusinessPartners, extractLines, extractPlans } from './utils';
import { IAssignedBPPlanInfo, ISalesforceAssignedBPPlanDrawerProps } from '.';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const SalesforceAssignedBPPlanDrawer: React.FC<
  ISalesforceAssignedBPPlanDrawerProps
> = ({ open, onSuccess, onClose, businessUserId }) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [values, setValues] = useState<IAssignedBPPlanInfo>(initialValues);
  const [lists, setLists] = useState<Record<string, Record<string, string>>>({
    primaryBP: {},
    line: {},
    plan: {},
  });
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(values || undefined, lists)
  );

  const [loading, setLoading] = useState<boolean>(true);

  const [getBusinessPartnersQuery] = useLazyQuery(
    getBusinessPartnerListWithoutPagination
  );
  const [getAssignedLineListLazy] = useLazyQuery(getBPLinesLov);
  const [getBPPlanListByAssignedLinesLazy] = useLazyQuery(
    getBPPlanListByAssignedLines
  );
  const [assignedBPPlanAction] = useMutation(createAssignedBPPlan);

  const initialize = async () => {
    setLoading(true);

    const businessPartnersLov = await getBusinessPartners();
    const newList: Record<string, Record<string, string>> = {
      primaryBP: businessPartnersLov,
      line: {},
      plan: {},
    };

    setLists(newList);
    setLoading(false);
    setInputsForm(drawerSections(values, newList));
  };

  useEffect(() => {
    initialize();
  }, []);

  const getBusinessPartners = async () => {
    const result = await getBusinessPartnersQuery({
      variables: {
        businessPartnerStatus: ['ACTIVE'],
      },
    });

    const extractedFetchedBusinessPartners = extractBusinessPartners(
      result.data
    );
    return extractedFetchedBusinessPartners || {};
  };

  const handleBusinessPartnerSelection = async (selectedOption: string) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getAssignedLineListLazy({
        variables: {
          selectedBusinessPartnerID: selectedOption,
        },
        errorPolicy: 'all',
      });
      return extractLines(newData.data);
    }
    return {} as Record<string, string>;
  };

  const handleLineSelection = async (
    selectedOption: string,
    businessPartner: string
  ) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getBPPlanListByAssignedLinesLazy({
        variables: {
          selectedBusienssUserID: businessUserId,
          selectedBusinessPartnerID: businessPartner,
          selectedLineID: selectedOption,
        },
        errorPolicy: 'all',
      });
      return extractPlans(newData.data);
    }
    return {} as Record<string, string>;
  };

  const onCustomChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    allValues: IAssignedBPPlanInfo
  ) => {
    const newLists = { ...lists };

    if (fieldName === 'businessPartner') {
      newLists['line'] = await handleBusinessPartnerSelection(value);
      allValues['line'] = '';

      allValues['plan'] = [];
      newLists['plan'] = {};
    }

    if (fieldName === 'line') {
      newLists['plan'] = await handleLineSelection(
        value,
        allValues.businessPartner
      );
      allValues['plan'] = [];
    }

    setValues(allValues);
    setLists(newLists);
    setInputsForm(drawerSections(allValues, newLists));
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(values as any, lists),
      values
    );
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const variables = {
        selectedBusinessPartnerID: data.businessPartner || null,
        selectedBusinessPartnerLineID: data.line || null,
        selectedBusinessPartnerPlanIDs: data.plan || [],
        selectedBusinessUserID: businessUserId,
      };

      const res = await assignedBPPlanAction({
        variables: variables,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Business Partner and Plan(s) successfully assigned.'}
          </ToastSuccessMessage>
        );

        setTimeout(() => {
          setFormDisabled(false);
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (err) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Assign Business Partner and Plan'}
      onClose={() => onClose()}
      isOpen={open}
      loading={loading}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      onChange={onCustomChange}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default SalesforceAssignedBPPlanDrawer;
